import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { Headline, Button, Text } from 'forly-kit';
import React, { useEffect, useState } from 'react';
import styles from './register.module.scss';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { SubmitHandler } from 'react-hook-form';
import { RegisterForm } from 'src/components/Forms/RegisterForm/RegisterForm';
import { IRegisterFormValues } from 'src/components/Forms/RegisterForm/registerForm.types';
import { IRegisterData } from 'types/common.types';
import { PasportLayout } from './../../components/PasportLayout/PasportLayout';
import { registrationApi } from './../../services/registerService';
import { colors } from 'forly-kit/dist/colors';
import { ErrorModal } from 'src/components/modals/ErrorModal/ErrorModal';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';

const RegisterComponent: React.FC = () => {
	const [reflink, setReflink] = useState<string>(CONFIG.defaultRef);
	const [registerErrorText, setRegisterErrorText] = useState<string>('');
	const [showSuccessRegister, setShowSuccessRegister] = useState<boolean>(false);
	const [captchaPending, setCaptchaPending] = useState<boolean>(false);
	const [registerEmail, setRegisterEmail] = useState<string>('');

	const { executeRecaptcha } = useGoogleReCaptcha();
	const {i18n} = useTranslation();

	const [
		register, 
		{isLoading: registerPending, isSuccess: successRegister, error: registerError}
	] = registrationApi.useRegisterMutation();

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const ref = params.get('ref');
		if (ref) {
			setReflink(ref);
		}
	}, []);

	useEffect(() => {
		if (successRegister) {
			setShowSuccessRegister(true);
		}
	}, [successRegister]);

	useEffect(() => {
		const error = registerError as FetchBaseQueryError;
		//@ts-ignore
		if (error && error.data.email_exists) {
			//@ts-ignore
			setRegisterErrorText(t('errors.sameEmail'));
			return;
		}

		if (error) {
			setRegisterErrorText(t('errors.unknownError'));
		}
	}, [registerError]);

	const goToPlatform = () => {
		window.location.href = `${CONFIG.lkUrl}/${i18n.language}/login/`;
	};

	const onSubmit: SubmitHandler<IRegisterFormValues> = async (data) => {
		if (!executeRecaptcha) {
			return;
		}

		if (!data.email) {
			return;
		}

		setRegisterErrorText('');

		setCaptchaPending(true);
		const token = await executeRecaptcha('yourAction');
		setCaptchaPending(false);

		const registerData: IRegisterData = {
			email: data.email,
			captcha: token,
			referral_link: reflink,
			lang: i18n.language
		};
		
		register(registerData);
		setRegisterEmail(data.email);
	};

	const closeErrorModal = () => {
		setRegisterErrorText('');
	};


	return <>
		<PasportLayout>
			{showSuccessRegister ?
				<>
					<Headline
						className={styles.title}
						type='h0'
						color={colors.primary800}
					>
						{t('successRegister')}
					</Headline>
					<Text className={styles.subtitle} size='m'>{t('successRegisterSubtitle', {email: registerEmail})}</Text>
					<Text className={styles.subtitle} size='m'>{t('goToEmail')}</Text>
				</>
				:<RegisterForm 
					action={onSubmit}
					isPending={registerPending || captchaPending}
				/>
			}
		</PasportLayout>
		
		{registerErrorText && <ErrorModal
			isOpen={!!registerErrorText}
			onClose={closeErrorModal}
			error={registerErrorText}
		/>}
	</> ;
};

export const RegisterPage = React.memo(RegisterComponent);
